import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { formatShopifyMoney, isClickedOutside } from "@theme/utilities";

/**
 * A custom element that shows an "Added to cart" confirmation popover anchored below the
 * header cart icon. Listens for incremental `cart:update` events (add-to-cart) and displays
 * the just-added item, a "View cart (N)" link and a "Checkout" button.
 *
 * Auto-dismisses after a timeout (paused while hovered/focused) and closes on the close
 * button, click-outside, and Escape — mirroring the localization dropdown pattern.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLImageElement} image
 * @property {HTMLElement} title
 * @property {HTMLElement} variant
 * @property {HTMLElement} price
 * @property {HTMLElement} count
 * @property {HTMLAnchorElement} viewBag
 *
 * @extends {Component<Refs>}
 */
class CartAddedPopoverComponent extends Component {
  #abortController = new AbortController();
  #dismissListeners = null;
  #dismissTimer = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    this.refs.closeButton?.addEventListener("click", this.hide, { signal });

    // Pause/resume the auto-dismiss timer while the pointer or focus is inside the popover.
    this.addEventListener("mouseenter", this.#clearTimer, { signal });
    this.addEventListener("focusin", this.#clearTimer, { signal });
    this.addEventListener("mouseleave", this.#startTimer, { signal });
    this.addEventListener("focusout", this.#startTimer, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#clearTimer();
    this.#unbindDismiss();
    super.disconnectedCallback();
  }

  get isHidden() {
    return this.hasAttribute("hidden");
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const detail = event.detail ?? {};

    // Only react to incremental adds (add-to-cart), not quantity edits / absolute refreshes.
    if (!detail.data?.isIncremental) return;
    if (detail.data?.didError) return;

    const item = this.#resolveItem(detail.resource);
    if (!item) return;

    this.#populate(item);
    this.#refreshCount();
    this.show();
  };

  /**
   * Resolves a single line item from the /cart/add.js response, handling the bundle
   * `{ items: [...] }` shape (use the last added item).
   *
   * @param {Object} resource
   * @returns {Object | null}
   */
  #resolveItem(resource) {
    if (!resource) return null;
    if (Array.isArray(resource.items) && resource.items.length) {
      return resource.items[resource.items.length - 1];
    }
    if (resource.product_title || resource.title) return resource;
    return null;
  }

  /**
   * @param {Object} item
   */
  #populate(item) {
    const { image, title, variant, price } = this.refs;

    const imageUrl = item.featured_image?.url || item.image;
    if (image) {
      if (imageUrl) {
        image.src = this.#sizedImage(imageUrl, 160);
        image.alt = item.featured_image?.alt || item.product_title || item.title || "";
        image.hidden = false;
      } else {
        image.removeAttribute("src");
        image.hidden = true;
      }
    }

    if (title) title.textContent = item.product_title || item.title || "";

    if (variant) {
      const variantTitle = item.variant_title;
      const showVariant = variantTitle && variantTitle !== "Default Title";
      variant.textContent = showVariant ? variantTitle : "";
      variant.hidden = !showVariant;
    }

    if (price) {
      const cents = item.final_price ?? item.price ?? 0;
      price.textContent = formatShopifyMoney(cents);
    }
  }

  /**
   * Fetches the cart to update the "View cart (N)" count.
   */
  #refreshCount() {
    const cartUrl = window.FoxTheme?.routes?.cart;
    if (!cartUrl) return;

    fetch(cartUrl)
      .then((response) => response.json())
      .then((cart) => {
        if (this.refs.count) this.refs.count.textContent = String(cart.item_count ?? "");
      })
      .catch((error) => console.error("Cart popover: failed to fetch cart count", error));
  }

  /**
   * Appends a width parameter to a Shopify CDN image URL.
   *
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #sizedImage(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set("width", String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }

  /**
   * Shows the popover and binds dismissal listeners.
   */
  show() {
    this.removeAttribute("hidden");
    // Force reflow so the transition runs when toggling the class.
    void this.offsetWidth;
    this.classList.add("is-visible");

    this.#bindDismiss();
    this.#startTimer();
  }

  /**
   * Hides the popover and unbinds dismissal listeners.
   */
  hide = () => {
    if (this.isHidden) return;

    this.classList.remove("is-visible");
    this.setAttribute("hidden", "");
    this.#clearTimer();
    this.#unbindDismiss();
  };

  #bindDismiss() {
    if (this.#dismissListeners) return;
    this.#dismissListeners = new AbortController();
    const { signal } = this.#dismissListeners;

    document.addEventListener("click", this.#handleClickOutside, { signal });
    document.addEventListener("keydown", this.#handleKeyDown, { signal });
  }

  #unbindDismiss() {
    this.#dismissListeners?.abort();
    this.#dismissListeners = null;
  }

  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) this.hide();
  };

  #handleKeyDown = (event) => {
    if (event.key === "Escape") this.hide();
  };

  #startTimer = () => {
    this.#clearTimer();
    const delay = Number(this.dataset.autodismiss) || 0;
    if (delay <= 0) return;
    this.#dismissTimer = setTimeout(this.hide, delay);
  };

  #clearTimer = () => {
    if (this.#dismissTimer) {
      clearTimeout(this.#dismissTimer);
      this.#dismissTimer = null;
    }
  };
}

if (!customElements.get("cart-added-popover")) {
  customElements.define("cart-added-popover", CartAddedPopoverComponent);
}
